import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import {
  ISbEventPayload,
  ISbStories,
  ISbStoriesParams,
  ISbStory,
  ISbStoryData,
  ISbStoryParams,
  StoryblokBridgeV2,
  renderRichText,
  storyblokInit,
} from '@storyblok/js';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  catchError,
  from,
  lastValueFrom,
  map,
  of,
  share,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { ISbComponentType } from 'storyblok-js-client';
import Stripe from 'stripe';
import { getAllRouteData } from '../lib/get-all-route-data';
import { getFullPath } from '../lib/get-full-path';
import { isIframe } from '../lib/is-iframe';
import { loadScript } from '../lib/load-script';
import { customRichTextSchema } from '../storyblok/custom-rich-text';
import { getRelationships } from '../storyblok/storyblok-find-components';
import { StoryblokLink } from '../storyblok/storyblok-types';
import { BrowserService } from './browser.service';
import { TransferStateService } from './transfer-state.service';

export interface StoryblokSectionStyle {
  _uid: string;
  advanced: string;
  component: string;
  pageWidth: string;
  background: string;
  topSpacing: string;
  bottomSpacing: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type StoryblokComponentInstance = InstanceType<any>;

@Injectable({
  providedIn: 'root',
})
export class StoryblokService {
  private route = inject(ActivatedRoute);
  accessToken = 'qit3qOMDswj7KNAg95r4yAtt';
  version: 'published' | 'draft' = 'published';
  linkMap = new Map<string, string>();
  components: { [key: string]: unknown } = {};

  tempUpdateCache = new Map<
    string,
    | ISbStoryData<
        ISbComponentType<string> & {
          [index: string]: unknown;
        }
      >[]
    | Stripe.Price[]
  >();

  // not using the client because it is super slow on the server
  private stories$ = new Map<string, Observable<ISbStory['data'] | false>>();
  private storiesByType$ = new Map<string, Observable<ISbStoryData[]>>();
  cacheVersion: number = undefined;

  private _update: Subject<ISbStoryData | undefined> = new BehaviorSubject<ISbStoryData | undefined>(undefined);
  public readonly onUpdates = this._update.asObservable();
  storyblokInstance: StoryblokBridgeV2;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private zone: NgZone,
    private transferState: TransferStateService,
    private http: HttpClient,
    private browserService: BrowserService
  ) {
    storyblokInit({
      richText: {
        schema: customRichTextSchema,
      },
    }); // can change in the future to add support for custom resolvers
    if (isPlatformServer(this.platformId)) return;
    if (!isIframe()) return;
    if (this.browserService.isExtension()) return;
    this.init();
  }

  async init() {
    await loadScript('https://app.storyblok.com/f/storyblok-v2-latest.js?t=Q2mIMuV6frKpnmQlOee0rAtt');
    console.log('Loading Storyblok JS for preview mode');

    const { StoryblokBridge, location } = window;

    this.storyblokInstance = new StoryblokBridge({
      preventClicks: true,
    });

    this.storyblokInstance.on(['change', 'published'], function () {
      location.reload();
    });

    this.storyblokInstance.on(['input'], (e: ISbEventPayload) => {
      this.zone.run(async () => {
        this.resolveRelationships(this.route.snapshot).subscribe(relationships => {
          this.tempUpdateCache.set('relationships', relationships);
          this._update.next(e.story);
        });
      });
    });
  }

  public getGlobalData(key: string) {
    if (this.tempUpdateCache.has(key)) {
      return this.tempUpdateCache.get(key);
    }
    const routeData = getAllRouteData(this.route.snapshot);
    return routeData[key];
  }

  private getStoryUrl(slug: string, params: ISbStoryParams) {
    const query = new URLSearchParams();
    if (params.resolve_links) {
      query.set('resolve_links', params.resolve_links);
    }

    return `https://api.storyblok.com/v2/cdn/stories/${slug}?${query.toString()}&token=${this.accessToken}&version=${
      params.version || 'published'
    }&cv=${this.cacheVersion}`;
  }

  private getStoryObservable(slug: string, params: ISbStoryParams): Observable<ISbStory['data'] | false> {
    return this.transferState
      .withStateTransferObservable(`storyblok-page-${slug}`, () =>
        this.http.get<ISbStory['data']>(this.getStoryUrl(slug, params))
      )
      .pipe(
        tap(res => {
          this.cacheVersion = res?.cv;
        }),
        catchError(() => {
          // console.error(err);
          return <Observable<undefined>>of(false);
        }),
        map(res => res || (false as const)),
        share({
          resetOnComplete: () => timer(0),
          connector: () => new ReplaySubject(1), // override default "new Subject()"
        })
      );
  }

  getStory(slug: string, route: ActivatedRouteSnapshot = this.route.snapshot): Observable<ISbStory['data'] | false> {
    if (!slug) return of(false);
    const version = route.queryParams['_storyblok'] ? 'draft' : 'published';
    if (!this.stories$.has(slug)) {
      this.stories$.set(slug, this.getStoryObservable(slug, { version }));
    }
    return this.stories$.get(slug);
  }

  private buildStoriesUrl(params: ISbStoriesParams) {
    const query = new URLSearchParams();
    if (params.content_type) {
      query.set('content_type', params.content_type);
    }
    if (params.starts_with) {
      query.set('starts_with', params.starts_with);
    }
    if (params.with_tag) {
      query.set('with_tag', params.with_tag);
    }
    if (params.page) {
      query.set('page', params.page.toString());
    }
    if (params.per_page) {
      query.set('per_page', params.per_page.toString());
    }
    if (params.excluding_fields) {
      query.set('excluding_fields', params.excluding_fields);
    }
    if (params.by_uuids) {
      query.set('by_uuids', params.by_uuids);
    }

    if (params.filter_query) {
      params.filter_query.forEach(item => {
        query.set(`filter_query[${item.key}][in]`, item.value);
      });
    }

    return `https://api.storyblok.com/v2/cdn/stories?${query.toString()}&token=${this.accessToken}&version=${
      params.version || 'published'
    }&cv=${this.cacheVersion}&resolve_links=url`;
  }

  private async getAllStories(params: ISbStoriesParams, page = 1, results = []): Promise<ISbStoryData[]> {
    const resultsPerPage = 100;
    const url = this.buildStoriesUrl({ ...params, page, per_page: resultsPerPage });
    const resp = await this.transferState.withStateTransfer(url, async () =>
      lastValueFrom(this.http.get<ISbStories['data']>(url))
    );

    resp.links.forEach(link => {
      this.linkMap.set(link.uuid, link['url']);
    });

    if (resp.stories.length === resultsPerPage) {
      return this.getAllStories(params, page + 1, [...results, ...resp.stories]);
    }
    return [...results, ...resp.stories];
  }

  private getStoriesObservable(params: ISbStoriesParams): Observable<ISbStoryData[]> {
    return from(this.getAllStories(params)).pipe(
      catchError(() => {
        // console.error(err);
        return <Observable<undefined>>of(undefined);
      }),
      share({
        resetOnComplete: () => timer(0),
        connector: () => new ReplaySubject(1), // override default "new Subject()"
      })
    );
  }

  getStories(
    options: ISbStoriesParams,
    route: ActivatedRouteSnapshot = this.route.snapshot
  ): Observable<ISbStoryData[]> {
    const version = route.queryParams['_storyblok'] ? 'draft' : 'published';
    const cacheKey = JSON.stringify({ ...options, version });
    if (!this.storiesByType$.has(cacheKey)) {
      this.storiesByType$.set(cacheKey, this.getStoriesObservable({ ...options, version }));
    }
    return this.storiesByType$.get(cacheKey);
  }

  public async resolveRelationshipsFromStory(story: ISbStoryData, route: ActivatedRouteSnapshot = this.route.snapshot) {
    if (!story) return [];

    let ids = Array.from(new Set(getRelationships(story))).filter(Boolean);
    const allStories = [story];

    while (ids.length) {
      const stories = await lastValueFrom(
        this.getStories(
          { by_uuids: ids.join(','), excluding_fields: 'body,preFooter,topBlur,blurOverlay' }, // excluding page fields because of previews where we anyway only need the meta data
          route
        )
      );

      ids = [];
      stories.forEach(_story => {
        ids.push(...getRelationships(_story));
      });
      ids = Array.from(new Set(ids)).filter(Boolean);
      allStories.push(...stories);
    }

    return allStories;
  }

  public resolveRelationships(route: ActivatedRouteSnapshot) {
    const path = getFullPath(route).join('/') || 'home';
    return this.getStory(path, route).pipe(
      switchMap(story => {
        if (!story) return of([]);
        return from(this.resolveRelationshipsFromStory(story.story, route));
      })
    );
  }

  private getAllComponentTypes(items: { component: string }[]): unknown[] {
    const componentTypes = new Set<unknown>();
    if (!items) return [];

    for (const item of items) {
      componentTypes.add(item.component as unknown);
      if (item.component === 'Text') {
        const subItems = (
          item['content'].content as { type: string; attrs: { body: { component: string }[] } }[]
        ).filter(item => item.type === 'blok');
        subItems.forEach(subItem => {
          const resp = this.getAllComponentTypes(subItem.attrs.body);
          resp.forEach(type => componentTypes.add(type));
        });
      }

      for (const key in item) {
        if (Array.isArray(item[key])) {
          const resp = this.getAllComponentTypes(item[key]);
          resp.forEach(type => componentTypes.add(type));
        }
      }
    }

    return Array.from(componentTypes);
  }

  private getBlocks(items: { component: string }[], components: string[], returnEarly = false) {
    const blocks = [];
    if (!items) return blocks;
    for (const item of items) {
      if (components.includes(item.component)) {
        blocks.push(item);
        if (returnEarly) return blocks;
      }
      for (const key in item) {
        if (Array.isArray(item[key])) {
          blocks.push(...this.getBlocks(item[key], components));
          if (returnEarly && blocks.length) return blocks;
        }
      }
    }
    return blocks;
  }

  public getStoryTitle(story: ISbStoryData): string {
    if (story.content.metaTitle) return story.content.metaTitle;
    const block = this.getBlocks(story.content.body, ['Title'], true)[0];
    return `${block?.title ?? story.name} · Picflow`;
  }

  public getStoryDescription(story: ISbStoryData): string {
    if (story.content.metaDescription) return story.content.metaDescription;
    const block = this.getBlocks(story.content.body, ['Text'], true)[0];
    if (!block?.content) {
      return 'The next-generation online gallery and proofing platform to boost creative workflows for freelancers and teams.';
    }
    return renderRichText(block.content).replace(/<[^>]*>?/gm, '');
  }

  public getUrlLink(link: StoryblokLink) {
    if (!link) return '';
    if (link.linktype === 'url') return link.url;
    if (link.linktype === 'story' && link.cached_url) {
      return '/' + (this.linkMap.get(link.id) || link.cached_url);
    }
    return '';
  }

  public async loadComponents(
    story: ISbStoryData<
      ISbComponentType<string> & {
        [index: string]: { component: string }[];
      }
    >
  ) {
    const componentTypes = this.getAllComponentTypes(story.content.body);
    await Promise.all(
      componentTypes.map(async type => {
        if (type === 'ContentGroup') {
          const { ContentGroupComponent } = await import(
            '../storyblok/components/content-group/content-group.component'
          );
          return (this.components[type] = ContentGroupComponent);
        }
        if (type === 'BadgeList') {
          const { BadgeListComponent } = await import('../storyblok/components/badge-list/badge-list.component');
          return (this.components[type] = BadgeListComponent);
        }
        if (type === 'BlogPostLinkList') {
          const { BlogPostLinkListComponent } = await import(
            '../storyblok/components/blog-post-link-list/blog-post-link-list.component'
          );
          return (this.components[type] = BlogPostLinkListComponent);
        }
        if (type === 'Blur') {
          const { StoryblokBlurComponent } = await import(
            '../storyblok/components/storyblok-blur/storyblok-blur.component'
          );
          return (this.components[type] = StoryblokBlurComponent);
        }
        if (type === 'Button') {
          const { StoryblokButtonComponent } = await import(
            '../storyblok/components/storyblok-button/storyblok-button.component'
          );
          return (this.components[type] = StoryblokButtonComponent);
        }
        if (type === 'CheckList') {
          const { CheckListComponent } = await import('../storyblok/components/check-list/check-list.component');
          return (this.components[type] = CheckListComponent);
        }
        if (type === 'Claim') {
          const { ClaimComponent } = await import('../storyblok/components/claim/claim.component');
          return (this.components[type] = ClaimComponent);
        }
        if (type === 'CompanyLogoList') {
          const { CompanyLogoListComponent } = await import(
            '../storyblok/components/company-logo-list/company-logo-list.component'
          );
          return (this.components[type] = CompanyLogoListComponent);
        }
        if (type === 'Comparison') {
          const { ComparisonComponent } = await import('../storyblok/components/comparison/comparison.component');
          return (this.components[type] = ComparisonComponent);
        }
        if (type === 'Conversion') {
          const { ConvertComponent } = await import('../tools/convert/components/convert/convert.component');
          return (this.components[type] = ConvertComponent);
        }
        if (type === 'ConversionFormatSwitcher') {
          const { ConversionFormatSwitcherComponent } = await import(
            '../tools/convert/components/conversion-format-switcher/conversion-format-switcher.component'
          );
          return (this.components[type] = ConversionFormatSwitcherComponent);
        }
        if (type === 'ConvertFromFormatList') {
          const { ConvertFromFormatListComponent } = await import(
            '../tools/convert/components/convert-from-format-list/convert-from-format-list.component'
          );
          return (this.components[type] = ConvertFromFormatListComponent);
        }
        if (type === 'ConvertToFormatList') {
          const { ConvertToFormatListComponent } = await import(
            '../tools/convert/components/convert-to-format-list/convert-to-format-list.component'
          );
          return (this.components[type] = ConvertToFormatListComponent);
        }
        if (type === 'FaqList') {
          const { FaqListComponent } = await import('../storyblok/components/faq-list/faq-list.component');
          return (this.components[type] = FaqListComponent);
        }
        if (type === 'FeatureItemList') {
          const { FeatureItemListComponent } = await import(
            '../storyblok/components/feature-item-list/feature-item-list.component'
          );
          return (this.components[type] = FeatureItemListComponent);
        }
        if (type === 'Form') {
          const { FormComponent } = await import('../storyblok/components/form/form.component');
          return (this.components[type] = FormComponent);
        }
        if (type === 'GalleryShowcaseList') {
          const { GalleryShowcaseListComponent } = await import(
            '../storyblok/components/gallery-showcase-list/gallery-showcase-list.component'
          );
          return (this.components[type] = GalleryShowcaseListComponent);
        }
        if (type === 'HomeTeaser') {
          const { HomeTeaserComponent } = await import('../storyblok/components/home-teaser/home-teaser.component');
          return (this.components[type] = HomeTeaserComponent);
        }
        if (type === 'IconWithTextList') {
          const { IconWithTextListComponent } = await import(
            '../storyblok/components/icon-with-text-list/icon-with-text-list.component'
          );
          return (this.components[type] = IconWithTextListComponent);
        }
        if (type === 'Image') {
          const { ImageComponent } = await import('../storyblok/components/image/image.component');
          return (this.components[type] = ImageComponent);
        }
        if (type === 'LargeLinkList') {
          const { LargeLinkListComponent } = await import(
            '../storyblok/components/large-link-list/large-link-list.component'
          );
          return (this.components[type] = LargeLinkListComponent);
        }
        if (type === 'PointList') {
          const { PointListComponent } = await import('../storyblok/components/point-list/point-list.component');
          return (this.components[type] = PointListComponent);
        }
        if (type === 'PinnedBlogPostLink') {
          const { PinnedBlogPostLinkComponent } = await import(
            '../storyblok/components/pinned-blog-post-link/pinned-blog-post-link.component'
          );
          return (this.components[type] = PinnedBlogPostLinkComponent);
        }
        if (type === 'PricingBoxList') {
          const { PricingBoxListComponent } = await import(
            '../storyblok/components/pricing-box-list/pricing-box-list.component'
          );
          return (this.components[type] = PricingBoxListComponent);
        }
        if (type === 'ProductComparison') {
          const { ProductComparisonListItemPreviewComponent } = await import(
            '../storyblok/components/product-comparison-list-item-preview/product-comparison-list-item-preview.component'
          );
          return (this.components[type] = ProductComparisonListItemPreviewComponent);
        }
        if (type === 'ProductComparisonList') {
          const { ProductComparisonListComponent } = await import(
            '../storyblok/components/product-comparison-list/product-comparison-list.component'
          );
          return (this.components[type] = ProductComparisonListComponent);
        }
        if (type === 'ProductFeatureList') {
          const { ProductFeatureListComponent } = await import(
            '../storyblok/components/product-feature-list/product-feature-list.component'
          );
          return (this.components[type] = ProductFeatureListComponent);
        }
        if (type === 'SubMenu') {
          const { SubMenuComponent } = await import('../storyblok/components/sub-menu/sub-menu.component');
          return (this.components[type] = SubMenuComponent);
        }
        if (type === 'TableOfContents') {
          const { TableOfContentsComponent } = await import(
            '../storyblok/components/table-of-contents/table-of-contents.component'
          );
          return (this.components[type] = TableOfContentsComponent);
        }
        if (type === 'Teaser') {
          const { TeaserComponent } = await import('../storyblok/components/teaser/teaser.component');
          return (this.components[type] = TeaserComponent);
        }
        if (type === 'Text') {
          const { TextComponent } = await import('../storyblok/components/text/text.component');
          return (this.components[type] = TextComponent);
        }
        if (type === 'Title') {
          const { TitleComponent } = await import('../storyblok/components/title/title.component');
          return (this.components[type] = TitleComponent);
        }
        if (type === 'Video') {
          const { VideoComponent } = await import('../storyblok/components/video/video.component');
          return (this.components[type] = VideoComponent);
        }
        if (type === 'MiniComparison') {
          const { MiniComparisonComponent } = await import(
            '../storyblok/components/mini-comparison/mini-comparison.component'
          );
          return (this.components[type] = MiniComparisonComponent);
        }
        // console.log(type);
      })
    );
  }
}
